import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Card, Col, Input, Modal, Result, Row, Tag, Typography } from "antd";
import { Terminal } from "xterm";
import "xterm/css/xterm.css";

import { resolveApiBase } from "../lib/apiBase";
import TerminalChatPanel from "./TerminalChatPanel";

const { Text } = Typography;
const API_BASE = resolveApiBase();
const OBSERVER_NAME_STORAGE_KEY = "datasnare:terminal-observer:display-name";

function websocketBase() {
    const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
    if (API_BASE.startsWith("http://") || API_BASE.startsWith("https://")) {
        const parsed = new URL(API_BASE);
        return `${parsed.protocol === "https:" ? "wss:" : "ws:"}//${parsed.host}`;
    }
    return `${scheme}//${window.location.host}`;
}

function readStoredName() {
    try {
        return localStorage.getItem(OBSERVER_NAME_STORAGE_KEY) || "";
    } catch {
        return "";
    }
}

export default function ObserverTerminalPage() {
    const hostRef = useRef(null);
    const termRef = useRef(null);
    const socketRef = useRef(null);
    const outputStartedRef = useRef(false);
    const [status, setStatus] = useState("Connecting");
    const [systemName, setSystemName] = useState("");
    const [error, setError] = useState("");
    const [displayName, setDisplayName] = useState(() => readStoredName());
    const [nameDraft, setNameDraft] = useState("");
    const [namePromptOpen, setNamePromptOpen] = useState(() => !readStoredName());
    const [chatMessages, setChatMessages] = useState([]);
    const [chatEnabled, setChatEnabled] = useState(true);
    const [chatDisplayName, setChatDisplayName] = useState("");
    const [connected, setConnected] = useState(false);

    const confirmName = useCallback(() => {
        const cleaned = String(nameDraft || "").trim().slice(0, 64);
        if (!cleaned) return;
        try {
            localStorage.setItem(OBSERVER_NAME_STORAGE_KEY, cleaned);
        } catch {
            // ignore storage failures
        }
        setDisplayName(cleaned);
        setNamePromptOpen(false);
    }, [nameDraft]);

    const sendChatMessage = useCallback((text) => {
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) return;
        socket.send(JSON.stringify({ type: "terminal.chat", payload: { message: text } }));
    }, []);

    useEffect(() => {
        if (namePromptOpen || !displayName) return undefined;

        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("observe_session");
        const observerToken = params.get("observe_token");
        if (!sessionId || !observerToken || !hostRef.current) {
            setStatus("Invalid invite");
            setError("This observer link is missing its session or invite token.");
            return undefined;
        }

        const term = new Terminal({
            cursorBlink: false,
            disableStdin: true,
            cols: 120,
            rows: 32,
            fontSize: 13,
            fontFamily: "Consolas, 'Courier New', monospace",
            theme: { background: "#0b1220", foreground: "#e2e8f0" },
            convertEol: false,
            scrollback: 5000,
        });
        term.open(hostRef.current);
        termRef.current = term;

        const socket = new WebSocket(
            `${websocketBase()}/ws/terminal/observer?session_id=${encodeURIComponent(sessionId)}`
            + `&observer_token=${encodeURIComponent(observerToken)}`
            + `&observer_name=${encodeURIComponent(displayName)}`,
        );
        socketRef.current = socket;
        socket.onopen = () => {
            setStatus("Connected");
            setConnected(true);
        };
        socket.onmessage = (event) => {
            let frame;
            try {
                frame = JSON.parse(event.data);
            } catch {
                return;
            }
            const frameType = String(frame?.type || "");
            const payload = frame?.payload || {};
            if (frameType === "terminal.status") {
                setSystemName(String(payload.system_name || ""));
                if (payload?.chat_enabled !== undefined) setChatEnabled(Boolean(payload.chat_enabled));
                if (payload?.chat_display_name) setChatDisplayName(String(payload.chat_display_name));
                const cols = Number(payload?.cols || term.cols || 120);
                const rows = Number(payload?.rows || term.rows || 32);
                try {
                    term.resize(cols, rows);
                } catch {
                    // ignore
                }
                setStatus(String(payload.mode || "view_only") === "view_only" ? "Connected" : String(payload.status || "Connected"));
            } else if (frameType === "terminal.stdout" || frameType === "terminal.stderr") {
                const data = String(payload.data || "");
                if (data) {
                    if (!outputStartedRef.current) {
                        outputStartedRef.current = true;
                        term.reset();
                    }
                    term.write(data);
                }
            } else if (frameType === "terminal.chat") {
                setChatMessages((current) => [...current, payload]);
            } else if (frameType === "terminal.chat.history") {
                setChatMessages(Array.isArray(payload?.messages) ? payload.messages : []);
            } else if (frameType === "terminal.error") {
                setError(String(payload.message || "Observer error"));
            } else if (frameType === "terminal.exit") {
                setStatus("Session ended");
                setConnected(false);
                term.writeln("\r\n[Session ended]");
            }
        };
        socket.onerror = () => {
            setStatus("Unavailable");
            setConnected(false);
            setError("The observer session could not be reached or has expired.");
        };
        socket.onclose = () => {
            setConnected(false);
            setStatus((current) => (current === "Connected" ? "Session ended" : current));
        };

        return () => {
            try {
                socket.close();
            } catch {
                // ignore
            }
            try {
                term.dispose();
            } catch {
                // ignore
            }
            socketRef.current = null;
            termRef.current = null;
        };
    }, [displayName, namePromptOpen]);

    if (error && status === "Invalid invite") {
        return <Result status="error" title="Invalid observer invite" subTitle={error} />;
    }

    return (
        <div style={{ minHeight: "100vh", background: "#0f172a", padding: 24, color: "#e2e8f0" }}>
            <Modal
                open={namePromptOpen}
                title="Join session"
                okText="Join"
                onOk={confirmName}
                okButtonProps={{ disabled: !String(nameDraft || "").trim() }}
                closable={false}
                maskClosable={false}
                cancelButtonProps={{ style: { display: "none" } }}
            >
                <Text type="secondary">
                    Enter the display name other participants will see next to your chat messages.
                </Text>
                <Input
                    value={nameDraft}
                    onChange={(event) => setNameDraft(event.target.value)}
                    onPressEnter={confirmName}
                    placeholder="e.g. Dana (Network Team)"
                    maxLength={64}
                    style={{ marginTop: 12 }}
                    autoFocus
                />
            </Modal>
            <Card
                title={<span style={{ color: "#e2e8f0" }}>DataSnare View-only Terminal{systemName ? ` - ${systemName}` : ""}</span>}
                extra={<Tag color={status === "Connected" ? "green" : "default"}>{status}</Tag>}
                styles={{ header: { background: "#111827", borderBottom: "1px solid #334155" }, body: { background: "#0b1220" } }}
            >
                {error ? <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} /> : null}
                <div style={{ marginBottom: 12 }}>
                    <Text style={{ color: "#94a3b8" }}>
                        Observer access is view-only. Terminal input, file transfer, and site navigation are disabled. You can chat with the operator.
                    </Text>
                </div>
                <Row gutter={16}>
                    <Col xs={24} lg={16}>
                        <div style={{ width: "100%", height: "calc(100vh - 260px)", minHeight: 380, overflow: "hidden", border: "1px solid #334155", background: "#0b1220" }}>
                            <div ref={hostRef} style={{ width: "100%", height: "100%", display: "block" }} />
                        </div>
                    </Col>
                    <Col xs={24} lg={8}>
                        <TerminalChatPanel
                            messages={chatMessages}
                            onSend={sendChatMessage}
                            connected={connected}
                            chatEnabled={chatEnabled}
                            displayName={chatDisplayName || displayName}
                            height={360}
                        />
                    </Col>
                </Row>
            </Card>
        </div>
    );
}
